import { isEnum, parseDate } from '@artup/utilities'
import { Entity } from '../common/entity'
import { Account } from './account'

/**
 * Security request types
 */
export const SecurityRequestType = {
  /**
   * Sign up
   */
  Signup: 'signup',
  /**
   * Activate account
   */
  ActivateAccount: 'activate-account',
  /**
   * New password
   */
  NewPassword: 'new-password'
}

/**
 * Security request
 */
export class SecurityRequest extends Entity {
  constructor (data = {}) {
    super(data)
    this.assign(data)
    if (!isEnum(SecurityRequestType, this.type)) throw new Error(`Invalid request type ${this.type}`)
  }

  /**
   * Creates an instance of {@link SecurityRequest}
   * using the specified data
   * @param {Object} data
   * @returns {SecurityRequest} Appropriate subclass of {@link SecurityRequest}
   */
  static create (data) {
    if (data) {
      switch (data.type) {
        case SecurityRequestType.Signup:
          return new SignupRequest(data)
        case SecurityRequestType.ActivateAccount:
          return new AccountActivationRequest(data)
        case SecurityRequestType.NewPassword:
          return new NewPasswordRequest(data)
        default:
          throw new Error(`Unsupported type [${data.type}]`)
      }
    }
  }

  normalize () {
    super.normalize()
    this.created = parseDate(this.created, new Date())
    this.used = this.used ? parseDate(this.used) : undefined
    this.account = this.cast(this.account, Account)
  }


  /**
   * Request token
   * @type {String}
   */
  token

  /**
   * Request type
   * @type {SecurityRequestType}
   */
  type

  /**
   * Account identifier
   * @type {Number}
   */
  accountId

  /**
   * Account name
   * @type {String}
   */
  accountName

  /**
   * Account details
   * @type {Account}
   */
  account

  /**
   * Validity of the request in minutes
   * @type {Number}
   */
  expiresAfter

  /**
   * Indicates that request has been used
   * @type {Date}
   */
  used

  /**
   * Additional data stored with the request
   * @type {Object}
   */
  data

  /**
   * Adds more data in {@link data} field, preserving what's already in there
   * @param {Object} data Object containing data to add
   */
  addData (data) {
    if (data) {
      this.data = {
        ...(this.data || {}),
        ...data
      }
    }
  }

  /**
   * Time left to expire, in minutes
   * @type {Number}
   */
  get timeToExpire () {
    const { created, expiresAfter, hasExpired } = this
    if (hasExpired) {
      return 0
    }

    const createdDate = parseDate(created)
    if (createdDate && expiresAfter) {
      const expiration = new Date(createdDate.getTime() + expiresAfter * 60000)
      return Math.floor((expiration - (new Date())) / 60000)
    }
  }

  /**
   * Returns true if request has expired
   * @type {Boolean}
   */
  get hasExpired () {
    const { created, expiresAfter, hasBeenUsed } = this
    if (hasBeenUsed) {
      return true
    }

    const createdDate = parseDate(created)
    if (createdDate && expiresAfter) {
      const now = new Date()
      const expiration = new Date(createdDate.getTime() + expiresAfter * 60000)
      return now >= expiration
    }

    return false
  }

  /**
   * Returns true if request has been used
   * @type {Boolean}
   */
  get hasBeenUsed () {
    return Boolean(this.used)
  }

  /**
   * Returns true if request can be used
   * @type {Boolean}
   */
  get canUse () {
    return !(this.hasBeenUsed || this.hasExpired)
  }
}

/**
 * Signup request
 */
export class SignupRequest extends SecurityRequest {
  constructor (data = {}) {
    super({
      ...data,
      type: SecurityRequestType.Signup
    })
  }
}

/**
 * New password request
 */
export class NewPasswordRequest extends SecurityRequest {
  constructor (data = {}) {
    super({
      ...data,
      type: SecurityRequestType.NewPassword
    })
  }
}

/**
 * Account activation request
 */
export class AccountActivationRequest extends SecurityRequest {
  constructor (data = {}) {
    super({
      ...data,
      type: SecurityRequestType.ActivateAccount
    })
  }
}
